/* eslint-disable prettier/prettier */
import { Level, Location } from '@/core/entities';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import {
	ChangeEvent,
	FunctionComponent,
	ReactElement,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { CustomerSitesResponse } from '@/api/customers/types';
import SelectInput from '@/components/ui/select-input';
import { ALL_CUSTOMERS_ID, ALL_SITES_ID } from '@/queries/logs';
import { useSite } from '@/queries/sites';
import RootLevelFilter from './RootLevelFilter';
import LevelFilter from './LevelFilter';

export interface SelectedLocation {
	level: Level;
	location?: Location;
}

interface LocationFilterDialogProps {
	open: boolean;
	onClose: () => void;
	applyLocationFilters: (locationFilters: SelectedLocation[]) => void;
	customerSites: CustomerSitesResponse[] | undefined;
	isAdmin?: boolean;
	onCustomerChange: (customerId: string) => void;
	onSiteChange: (siteId: string) => void;
	initialCustomerId?: string;
	initialSiteId?: string;
	initialLocations?: SelectedLocation[];
}

const LocationFiltersDialog: FunctionComponent<LocationFilterDialogProps> = ({
	open,
	onClose,
	applyLocationFilters,
	customerSites,
	isAdmin,
	onSiteChange,
	onCustomerChange,
	initialCustomerId,
	initialSiteId,
	initialLocations,
}): ReactElement => {
	const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(
		initialSiteId ?? ALL_SITES_ID
	);
	const [selectedLocations, setSelectedLocations] = useState<SelectedLocation[]>([]);
	const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>(
		initialCustomerId ?? ALL_CUSTOMERS_ID
	);
	const restoredLocations = useRef(false);

	const siteId = useMemo(() => {
		if (selectedSiteId) return selectedSiteId;
		const firstCustomerWithSites = customerSites?.find((customer) => customer.sites.length);
		return firstCustomerWithSites?.sites?.[0].id;
	}, [customerSites, selectedSiteId]);

	const { query: siteQuery } = useSite(siteId, siteId !== ALL_SITES_ID);
	const { data: siteData } = siteQuery;

	const site = useMemo(() => (siteId === ALL_SITES_ID ? undefined : siteData), [siteData, siteId]);

	useEffect(() => {
		if (
			!restoredLocations.current &&
			siteId === initialSiteId &&
			site?.levels?.length &&
			initialLocations?.length === site.levels.length
		) {
			restoredLocations.current = true;
			setSelectedLocations(initialLocations);
			return;
		}
		setSelectedLocations(site?.levels?.map((level) => ({ level, location: undefined })) || []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [site]);

	const customerOptions = [
		{ value: ALL_CUSTOMERS_ID, label: 'All Clients' },
		...(customerSites ?? []).map((customer) => ({
			value: customer.id,
			label: customer.name,
		})),
	];

	const selectedCustomer = useMemo(() => {
		if (!customerSites || !customerSites.length) return undefined;
		if (!isAdmin) {
			return customerSites[0];
		}
		if (selectedCustomerId === ALL_CUSTOMERS_ID) return undefined;
		return customerSites.find((c) => c.id === selectedCustomerId);
	}, [customerSites, selectedCustomerId, isAdmin]);

	const siteOptions =
		selectedCustomer
			? [
				{ value: ALL_SITES_ID, label: 'All Sites' },
				...(selectedCustomer.sites ?? []).map((site) => ({
					value: site.id,
					label: site.name,
				})),
			]
			: [{ value: ALL_SITES_ID, label: 'All Sites' }];

	const onLocationSelected = (location: Location | undefined, index: number) => {
		const updatedLocations = selectedLocations.map((selectedLocation, key) => {
			if (key === index) return { ...selectedLocation, location };
			if (key > index) return { ...selectedLocation, location: undefined };
			return selectedLocation;
		});
		setSelectedLocations(updatedLocations);
	};

	const renderCustomerSelect = () => (
		<SelectInput
			key="client-select"
			variant="filled"
			fullWidth
			label="Client"
			options={customerOptions}
			value={selectedCustomerId}
			size="small"
			onChange={(event: ChangeEvent<HTMLInputElement>) => {
				setSelectedCustomerId(event.target.value);
				setSelectedSiteId(ALL_SITES_ID);
			}}
		/>
	);

	const renderSiteSelect = () => (
		<SelectInput
			key="site-select"
			variant="filled"
			fullWidth
			label="Site"
			options={siteOptions}
			value={selectedSiteId}
			size="small"
			onChange={(event: ChangeEvent<HTMLInputElement>) => {
				setSelectedSiteId(event.target.value);
			}}
			sx={{ display: isAdmin && selectedCustomerId === ALL_CUSTOMERS_ID ? 'none' : 'block' }}
		/>
	);

	const filters = () => [
		isAdmin && renderCustomerSelect(),
		renderSiteSelect(),
		...selectedLocations.map((selectedLocation, index) => {
			const props = {
				key: index,
				level: selectedLocation.level,
				selectedLocation: selectedLocations[index].location?.id,
				setLocation: (location: Location | undefined) => onLocationSelected(location, index),
			};

			return index === 0 ? (
				<RootLevelFilter {...props} />
			) : (
				<LevelFilter {...{ ...props, parentId: selectedLocations[index - 1].location?.id }} />
			);
		}),
	];

	const onLocationFiltersApply = () => {
		applyLocationFilters(selectedLocations);
		onSiteChange(selectedSiteId ?? ALL_SITES_ID);
		onCustomerChange(selectedCustomerId ?? ALL_CUSTOMERS_ID);
		onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>Additional filters</DialogTitle>
			<DialogContent>
				<Stack direction="column" spacing={2}>
					{filters()}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="text" onClick={onClose}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={() => {
						onLocationFiltersApply();
					}}
				>
					Apply
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default LocationFiltersDialog;
